<script setup>
import { ref, reactive, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLeadGeneration } from '@composables/useLeadGeneration'
import ErrorModal from '@components/ErrorModal.vue'
import MarkdownModal from '@components/MarkdownModal.vue'

const props = defineProps({
  publicId: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['success'])

const { t } = useI18n()

const {
  formData,
  errors,
  isSubmitting,
  submitError,
  isSuccess,
  isFormValid,
  fieldConfig,
  handleBlur,
  handleInput,
  submitForm
} = useLeadGeneration(props.publicId)

// Error modal state
const showErrorModal = ref(false)

// Success animation state
const showSuccessAnimation = ref(false)

// Get markdown URLs from environment
const privacyUrl = import.meta.env.VITE_APP_PRIVACY_MD_URL || ''
const termsUrl = import.meta.env.VITE_APP_TERMS_MD_URL || ''
const marketingUrl = import.meta.env.VITE_APP_MARKETING_MD_URL || ''

// Markdown modal state
const modalState = reactive({
  isOpen: false,
  type: null, // 'privacy' | 'terms' | 'marketing'
  title: '',
  url: ''
})

// Open markdown modal
const openMarkdown = type => {
  const configs = {
    privacy: {
      title: t('leadForm.privacy.modalTitle'),
      url: privacyUrl
    },
    terms: {
      title: t('leadForm.terms.modalTitle'),
      url: termsUrl
    },
    marketing: {
      title: t('leadForm.marketing.modalTitle'),
      url: marketingUrl
    }
  }

  const config = configs[type]
  if (!config || !config.url) {
    if (import.meta.env.DEV) {
      console.warn(`No URL configured for ${type}`)
    }
    return
  }

  modalState.type = type
  modalState.title = config.title
  modalState.url = config.url
  modalState.isOpen = true
}

// Close markdown modal
const closeModal = () => {
  modalState.isOpen = false
}

// Handle form submission
const handleSubmit = async e => {
  e.preventDefault()

  const success = await submitForm()

  if (success) {
    // Show success animation
    showSuccessAnimation.value = true

    // Wait for animation, then emit success
    setTimeout(() => {
      emit('success')
    }, 1500)
  } else if (submitError.value) {
    // Show error modal
    showErrorModal.value = true
  }
}

// Close error modal
const closeErrorModal = () => {
  showErrorModal.value = false
}

// Watch for submit errors
watch(submitError, newError => {
  if (newError) {
    showErrorModal.value = true
  }
})
</script>

<template>
  <div class="min-h-full flex items-center justify-center p-4">
    <!-- Success Animation Overlay -->
    <Transition
      enter-active-class="transition-opacity duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-300"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="showSuccessAnimation"
        class="fixed inset-0 z-50 flex items-center justify-center bg-theme-primary-90 backdrop-blur-sm"
      >
        <div class="text-center">
          <!-- Checkmark Animation -->
          <div
            class="inline-flex items-center justify-center w-24 h-24 rounded-full bg-green-100 dark:bg-green-900/30 mb-4"
          >
            <svg
              class="w-12 h-12 text-green-600 dark:text-green-400 animate-bounce"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="3"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <p class="text-xl font-semibold text-theme-primary">
            {{ t('leadForm.success') }}
          </p>
        </div>
      </div>
    </Transition>

    <!-- Loading Overlay -->
    <Transition
      enter-active-class="transition-opacity duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-300"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isSubmitting"
        class="fixed inset-0 z-40 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      >
        <div class="bg-theme-primary rounded-2xl p-8 shadow-2xl text-center">
          <div
            class="animate-spin rounded-full h-16 w-16 border-b-4 loading-spinner mx-auto mb-4"
          ></div>
          <p class="text-lg font-medium text-theme-primary">
            {{ t('leadForm.submitting') }}
          </p>
        </div>
      </div>
    </Transition>

    <!-- Form Container -->
    <div
      class="w-full max-w-md bg-theme-primary rounded-2xl shadow-xl p-6 border border-theme-default transition-all duration-300"
    >
      <!-- Header -->
      <div class="text-center mb-6">
        <h2 class="text-2xl font-bold text-theme-primary mb-2">
          {{ t('leadForm.title') }}
        </h2>
        <p class="text-sm text-theme-secondary">
          {{ t('leadForm.subtitle') }}
        </p>
      </div>

      <!-- Form -->
      <form @submit="handleSubmit" class="space-y-4">
        <!-- First Name -->
        <div>
          <label for="firstName" class="block text-sm font-medium text-input-label mb-1">
            {{ t('leadForm.firstName') }}
            <span class="text-required">*</span>
          </label>
          <input
            id="firstName"
            type="text"
            :value="formData.firstName"
            @input="handleInput('firstName', $event.target.value)"
            @blur="handleBlur('firstName')"
            autocomplete="given-name"
            :class="[
              'w-full px-4 py-3 rounded-lg border transition-all duration-200',
              errors.firstName ? 'border-error focus:border-error' : 'border-theme-default',
              'bg-theme-primary text-theme-primary'
            ]"
            :placeholder="t('leadForm.firstName')"
          />
          <p v-if="errors.firstName" class="mt-1 text-sm text-error">
            {{ errors.firstName }}
          </p>
        </div>

        <!-- Last Name -->
        <div>
          <label for="lastName" class="block text-sm font-medium text-input-label mb-1">
            {{ t('leadForm.lastName') }}
            <span class="text-required">*</span>
          </label>
          <input
            id="lastName"
            type="text"
            :value="formData.lastName"
            @input="handleInput('lastName', $event.target.value)"
            @blur="handleBlur('lastName')"
            autocomplete="family-name"
            :class="[
              'w-full px-4 py-3 rounded-lg border transition-all duration-200',
              errors.lastName
                ? 'border-error dark:border-error focus:border-error'
                : 'border-theme-default',
              'bg-theme-primary text-theme-primary'
            ]"
            :placeholder="t('leadForm.lastName')"
          />
          <p v-if="errors.lastName" class="mt-1 text-sm text-error">
            {{ errors.lastName }}
          </p>
        </div>

        <!-- Email OR Phone Label (only shown for "both" mode) -->
        <div v-if="fieldConfig.eitherRequired" class="text-sm font-medium text-input-label">
          {{ t('leadForm.emailOrPhoneRequired') }}
        </div>

        <!-- Email field - conditionally rendered -->
        <div v-if="fieldConfig.showEmail">
          <label for="email" class="block text-sm font-medium text-input-label mb-1">
            {{ t('leadForm.email') }}
            <span v-if="fieldConfig.emailRequired" class="text-required">*</span>
            <span v-else-if="fieldConfig.eitherRequired" class="text-theme-tertiary text-xs">
              ({{ t('leadForm.optional') }})
            </span>
          </label>
          <input
            id="email"
            type="email"
            inputmode="email"
            :value="formData.email"
            @input="handleInput('email', $event.target.value)"
            @blur="handleBlur('email')"
            autocomplete="email"
            :required="fieldConfig.emailRequired"
            :class="[
              'w-full px-4 py-3 rounded-lg border transition-all duration-200',
              errors.email ? 'border-error focus:border-error' : 'border-theme-default',
              'bg-theme-primary text-theme-primary'
            ]"
            :placeholder="t('leadForm.emailPlaceholder')"
          />
          <p v-if="errors.email" class="mt-1 text-sm text-error">
            {{ errors.email }}
          </p>
        </div>

        <!-- Phone field - conditionally rendered -->
        <div v-if="fieldConfig.showPhone">
          <label for="phone" class="block text-sm font-medium text-input-label mb-1">
            {{ t('leadForm.phone') }}
            <span v-if="fieldConfig.phoneRequired" class="text-required">*</span>
            <span v-else-if="fieldConfig.eitherRequired" class="text-theme-tertiary text-xs">
              ({{ t('leadForm.optional') }})
            </span>
          </label>
          <input
            id="phone"
            type="tel"
            inputmode="tel"
            :value="formData.phone"
            @input="handleInput('phone', $event.target.value)"
            @blur="handleBlur('phone')"
            autocomplete="tel"
            :required="fieldConfig.phoneRequired"
            :class="[
              'w-full px-4 py-3 rounded-lg border transition-all duration-200',
              errors.phone ? 'border-error focus:border-error' : 'border-theme-default',
              'bg-theme-primary text-theme-primary'
            ]"
            :placeholder="t('leadForm.phonePlaceholder')"
          />
          <p v-if="errors.phone" class="mt-1 text-sm text-error">
            {{ errors.phone }}
          </p>
        </div>

        <!-- Error message for "both" mode when neither field is filled -->
        <div v-if="fieldConfig.eitherRequired && errors.emailOrPhone">
          <p class="text-sm text-error">
            {{ errors.emailOrPhone }}
          </p>
        </div>

        <!-- Checkboxes Section -->
        <div class="space-y-4 pt-2">
          <!-- Privacy Policy Checkbox - Required -->
          <div class="flex items-start space-x-3">
            <input
              id="privacy"
              v-model="formData.privacyAccepted"
              type="checkbox"
              required
              class="mt-1 h-4 w-4 rounded border-theme-default text-theme-primary focus:ring-offset-2 transition-all"
            />
            <div class="flex-1">
              <label for="privacy" class="text-sm text-input-label">
                <span>{{ t('leadForm.privacy.label') }}</span>
                <button
                  v-if="privacyUrl"
                  type="button"
                  @click="openMarkdown('privacy')"
                  class="text-link underline ml-1 transition-colors"
                  :aria-label="
                    t('leadForm.privacy.link') + ' - ' + t('leadForm.modal.opensInModal')
                  "
                >
                  {{ t('leadForm.privacy.link') }}
                </button>
                <span class="text-required ml-1">*</span>
              </label>
              <p v-if="errors.privacy" class="mt-1 text-sm text-error">
                {{ errors.privacy }}
              </p>
            </div>
          </div>

          <!-- Terms & Conditions Checkbox - Conditionally Required -->
          <div v-if="fieldConfig.termsRequired === 'true'" class="flex items-start space-x-3">
            <input
              id="terms"
              v-model="formData.termsAccepted"
              type="checkbox"
              required
              class="mt-1 h-4 w-4 rounded text-theme-primary focus:ring-offset-2 transition-all"
            />
            <div class="flex-1">
              <label for="terms" class="text-sm text-input-label">
                <span>{{ t('leadForm.terms.label') }}</span>
                <button
                  v-if="termsUrl"
                  type="button"
                  @click="openMarkdown('terms')"
                  class="text-link underline ml-1 transition-colors"
                  :aria-label="t('leadForm.terms.link') + ' - ' + t('leadForm.modal.opensInModal')"
                >
                  {{ t('leadForm.terms.link') }}
                </button>
                <span class="text-required ml-1">*</span>
              </label>
              <p v-if="errors.terms" class="mt-1 text-sm text-error">
                {{ errors.terms }}
              </p>
            </div>
          </div>

          <!-- Marketing Opt-in Checkbox - Optional -->
          <div
            class="flex items-start space-x-3 bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg border border-theme-default"
          >
            <input
              id="marketing"
              v-model="formData.marketingOptIn"
              type="checkbox"
              class="mt-1 h-4 w-4 rounded border-theme-default text-theme-primary focus:ring-offset-2 transition-all"
            />
            <div class="flex-1">
              <label for="marketing" class="text-sm text-input-label">
                <span>{{ t('leadForm.marketing.label') }}</span>
                <button
                  v-if="marketingUrl"
                  type="button"
                  @click="openMarkdown('marketing')"
                  class="text-link underline ml-1 transition-colors"
                  :aria-label="
                    t('leadForm.marketing.link') + ' - ' + t('leadForm.modal.opensInModal')
                  "
                >
                  {{ t('leadForm.marketing.link') }}
                </button>
              </label>
              <p class="text-xs text-theme-tertiary mt-1">
                {{ t('leadForm.marketing.description') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <button
          type="submit"
          :disabled="!isFormValid || isSubmitting"
          :class="[
            'w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl cursor-pointer'
          ]"
        >
          {{ isSubmitting ? t('leadForm.submitting') : t('leadForm.submit') }}
        </button>
      </form>
    </div>

    <!-- Error Modal -->
    <ErrorModal :isOpen="showErrorModal" :errorMessage="submitError" @close="closeErrorModal" />

    <!-- Markdown Modal -->
    <MarkdownModal
      :isOpen="modalState.isOpen"
      :url="modalState.url"
      :title="modalState.title"
      @close="closeModal"
    />
  </div>
</template>

<style scoped>
/* Form Container */
.bg-theme-primary {
  background-color: var(--color-theme-bg-primary);
}
.bg-theme-primary-90 {
  background-color: color-mix(in oklab, var(--color-theme-bg-primary) 90%, transparent);
}

.text-theme-primary {
  color: var(--color-theme-primary-text);
}

.text-theme-secondary {
  color: var(--color-theme-secondary-text);
}

.text-theme-tertiary {
  color: var(--color-theme-text-tertiary);
}

.text-input-label {
  color: var(--theme-form-label-text);
}

.border-theme-default {
  border-color: var(--color-theme-border);
}

/* Input Fields */
input[type='text'],
input[type='email'],
input[type='tel'] {
  background-color: var(--theme-form-input-bg);
  border-color: var(--theme-form-input-border);
  color: var(--theme-form-input-text);
  transition: all 0.2s;
}

input[type='text']::placeholder,
input[type='email']::placeholder,
input[type='tel']::placeholder {
  color: var(--theme-form-input-placeholder);
}

input[type='text']:focus,
input[type='email']:focus,
input[type='tel']:focus {
  border-color: var(--theme-form-input-border-focus);
  box-shadow: var(--theme-form-input-border-focus);
  outline: none;
}

/* Error State Inputs */
input.border-error {
  border-color: var(--theme-form-error-border) !important;
  background-color: var(--theme-form-error-bg);
}

/* Labels */
label {
  color: var(--theme-form-label-text);
}

/* Required Asterisk */
.text-required {
  color: var(--theme-form-label-required);
}

/* Error Messages */
.text-error {
  color: var(--theme-form-error-text);
}

/* Submit Button - Primary */
button[type='submit']:not(:disabled) {
  background-color: var(--theme-button-primary-bg);
  color: var(--color-theme-text-inverted);
  border-color: var(--theme-button-primary-border);
}

button[type='submit']:not(:disabled):hover {
  background-color: var(--theme-button-primary-bg-hover);
}

/* Submit Button - Disabled */
button[type='submit']:disabled {
  background-color: var(--theme-button-primary-bg);
  color: var(--theme-button-primary-text);
  opacity: 0.3;
  cursor: not-allowed;
}

/* Markdown Link Buttons */
button[type='button'].text-link {
  color: var(--theme-primary-color);
}

button[type='button'].text-link:hover {
  color: var(--theme-primary-hover);
}

/* Checkbox */
input[type='checkbox'] {
  accent-color: var(--color-theme-primary);
  border-color: var(--theme-form-input-border);
  color: var(--theme-button-primary-bg);
}

input[type='checkbox']:focus {
  box-shadow: var(--theme-button-primary-bg);
}

/* Loading Spinner */
.loading-spinner {
  border-color: var(--color-theme-primary);
}
</style>
